import { z } from 'zod';
import { Avatar, FleetSide, IsoDateTime } from './common.ts';

export const GAME_STATUSES = ['pending', 'active', 'finished', 'abandoned'] as const;
export const GameStatus = z.enum(GAME_STATUSES);
export type GameStatus = z.infer<typeof GameStatus>;

/**
 * `hotseat`: one account plays both sides at one screen; `bot`: one account against the
 * computer, which the server plays; `online`: two accounts, one side each.
 */
export const GAME_MODES = ['hotseat', 'bot', 'online'] as const;
export const GameMode = z.enum(GAME_MODES);
export type GameMode = z.infer<typeof GameMode>;

/** The games an account can start: an online one waits for whoever takes its invite. */
export const NEW_GAME_MODES = ['hotseat', 'bot', 'online'] as const satisfies readonly GameMode[];
export type NewGameMode = (typeof NEW_GAME_MODES)[number];

export const PlayerSummary = z.object({
  id: z.uuid(),
  nickname: z.string(),
  displayName: z.string(),
  avatar: Avatar,
});
export type PlayerSummary = z.infer<typeof PlayerSummary>;

/** Whose move it is from the requesting player's point of view; `null` when nobody is to move. */
export const TurnOwner = z.enum(['mine', 'theirs']).nullable();
export type TurnOwner = z.infer<typeof TurnOwner>;

export const GameSummary = z.object({
  id: z.uuid(),
  mode: GameMode,
  /** `null` while an invite is still open, and in hot-seat and computer games. */
  opponent: PlayerSummary.nullable(),
  status: GameStatus,
  mySide: FleetSide,
  turn: TurnOwner,
  turnNumber: z.int().nonnegative(),
  updatedAt: IsoDateTime,
});
export type GameSummary = z.infer<typeof GameSummary>;

export const MyGamesDto = z.object({
  items: z.array(GameSummary),
});
export type MyGamesDto = z.infer<typeof MyGamesDto>;

// ── Playing a game on the server ────────────────────────────────────────────
// The rules engine (@brig/rules-engine) is the authority on what is legal; these schemas only
// check the shape of what a client sends, so the engine never sees malformed input.

const Id = z.string().min(1).max(64);
const Cell = z.string().regex(/^[A-Z]{1,2}[1-9][0-9]{0,2}$/);
const Shots = z.int().min(1).max(99);

/** One engine action (`Action` in @brig/rules-engine). */
export const GameAction = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('declareMoveGroup'),
    side: FleetSide,
    shipIds: z.array(Id).max(64),
  }),
  z.strictObject({ type: z.literal('spinForMovement'), side: FleetSide }),
  z.strictObject({ type: z.literal('moveShip'), side: FleetSide, shipId: Id, to: Cell }),
  z.strictObject({ type: z.literal('moveInPort'), side: FleetSide, shipId: Id, to: Cell }),
  z.strictObject({ type: z.literal('fire'), side: FleetSide, shooterId: Id, targetId: Id }),
  z.strictObject({
    type: z.literal('volley'),
    side: FleetSide,
    shooters: z.array(z.strictObject({ id: Id, shots: Shots })).max(64),
    targetId: Id,
  }),
  z.strictObject({ type: z.literal('enterPortRepair'), side: FleetSide, shipId: Id }),
  z.strictObject({
    type: z.literal('startFortRepair'),
    side: FleetSide,
    fortId: Id,
    crew: z.array(Id).max(64),
  }),
  z.strictObject({ type: z.literal('stopFortRepair'), side: FleetSide, fortId: Id }),
  z.strictObject({ type: z.literal('endTurn'), side: FleetSide }),
  z.strictObject({ type: z.literal('resign'), side: FleetSide }),
  z.strictObject({ type: z.literal('offerDraw'), side: FleetSide }),
  z.strictObject({ type: z.literal('acceptDraw'), side: FleetSide }),
]);
export type GameAction = z.infer<typeof GameAction>;

const Uint32 = z.int().min(0).max(0xffffffff);

/** A recorded action, with the PRNG state the server dealt for it if it used chance (R-RNG-03). */
export const GameStep = z.strictObject({
  action: GameAction,
  rng: z.tuple([Uint32, Uint32, Uint32, Uint32]).optional(),
});
export type GameStep = z.infer<typeof GameStep>;

const BySide = <T extends z.ZodType>(value: T) => z.strictObject({ red: value, blue: value });

/** Presentation only: each side's nation (players are named after it) and colour on the board. */
export const Nations = BySide(z.string().min(1).max(32));
export type Nations = z.infer<typeof Nations>;
export const Colours = BySide(z.string().min(1).max(32));
export type Colours = z.infer<typeof Colours>;

export const GAME_SIZES = ['small', 'medium', 'large'] as const;

/** The rule options a new game may change (§16 of docs/rules.md). Dev tools only. */
export const RuleOptions = z.strictObject({
  oarsAndSteamUseBest: z.boolean().optional(),
  passThroughShips: z.boolean().optional(),
  landBlocksFire: z.boolean().optional(),
  diagonalSqueeze: z.boolean().optional(),
  hitChance: z.number().min(0).max(1).optional(),
});
export type RuleOptions = z.infer<typeof RuleOptions>;

export const CreateGameBody = z.strictObject({
  /**
   * Hot seat by default; against the computer, the player takes the red side's nation. Online,
   * the creator plays red and the game is pending until someone accepts its invite.
   */
  mode: z.enum(NEW_GAME_MODES).optional(),
  size: z.enum(GAME_SIZES).optional(),
  nations: Nations.optional(),
  colours: Colours.optional(),
  /** Dev tools only: a fixed seed for the setup, and rule options. */
  seed: z.int().min(0).max(0x7fffffff).optional(),
  rules: RuleOptions.optional(),
});
export type CreateGameBody = z.infer<typeof CreateGameBody>;

/** The link that brings the second player into an online game: `/join/<code>`. */
export const InviteCode = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/);

/** An online game's open invite, as its creator sees it while nobody has taken it. */
export const OpenInvite = z.object({ code: InviteCode, expiresAt: IsoDateTime });
export type OpenInvite = z.infer<typeof OpenInvite>;

/**
 * A whole game as the server keeps it: the setup (`GameSetup` in @brig/rules-engine) and the
 * steps since. Replaying the steps gives every position and event (R-RNG-03).
 */
export const GameDto = z.object({
  id: z.uuid(),
  mode: GameMode,
  /** The side the requesting player plays; in a hot-seat game they play both. */
  mySide: FleetSide,
  status: GameStatus,
  /** Bumped by every accepted change; sent back with the next one. */
  version: z.int().nonnegative(),
  setup: z.looseObject({ seed: z.int(), map: z.looseObject({ id: z.string() }) }),
  nations: Nations,
  colours: Colours,
  steps: z.array(GameStep),
  /** The open invite, for the creator of an online game still waiting for an opponent. */
  invite: OpenInvite.nullable(),
  /** The server allows the dev tools: rewind, fixed seeds, rule options, importing a log. */
  devTools: z.boolean(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type GameDto = z.infer<typeof GameDto>;

/** Several actions as one intent: all of them, or none if any is illegal. */
export const ActBody = z.strictObject({
  version: z.int().nonnegative(),
  actions: z.array(GameAction).min(1).max(16),
});
export type ActBody = z.infer<typeof ActBody>;

export const ActDto = z.object({
  version: z.int().nonnegative(),
  steps: z.array(GameStep),
});
export type ActDto = z.infer<typeof ActDto>;

/**
 * Takes back the last step. `undo` only goes back over actions without chance since the last
 * one with it (docs/ux.md U-UNDO-01); `rewind` (dev tools) takes back anything.
 */
export const UndoBody = z.strictObject({
  version: z.int().nonnegative(),
  kind: z.enum(['undo', 'rewind']).default('undo'),
});
export type UndoBody = z.input<typeof UndoBody>;

export const UndoDto = z.object({
  version: z.int().nonnegative(),
  /** Steps taken back: one, or against the computer a rewind takes its answer too. */
  taken: z.int().positive(),
});
export type UndoDto = z.infer<typeof UndoDto>;

export const UpdateGameBody = z.strictObject({ version: z.int().nonnegative(), colours: Colours });
export type UpdateGameBody = z.infer<typeof UpdateGameBody>;

/**
 * An invite as anyone holding its link sees it, signed in or not: who invites, to which game,
 * and whether it can still be taken.
 */
export const InviteDto = z.object({
  code: InviteCode,
  status: z.enum(['pending', 'accepted', 'declined', 'expired', 'revoked']),
  inviter: PlayerSummary,
  /** The inviter's nation (red) and the one the invitee would command (blue). */
  nations: Nations,
  size: z.enum(GAME_SIZES).nullable(),
  expiresAt: IsoDateTime,
});
export type InviteDto = z.infer<typeof InviteDto>;

/** Rule violation details in a `rule_violation` error (`RuleViolationInfo` in the engine). */
export const RuleViolationDetails = z.object({
  rule: z.string(),
  reason: z.string(),
  message: z.string(),
});
export type RuleViolationDetails = z.infer<typeof RuleViolationDetails>;

// ── Game logs: a file to keep, or to load again (dev tools) ─────────────────

export const LOG_FORMAT = 'brig-sandbox-log';

const LogPlayer = z.object({ id: z.string().max(64), name: z.string().max(64) });

const LogSetup = z.object({
  /** Absent in logs from before game sizes: those replay on the map they carry. */
  size: z.enum(GAME_SIZES).optional(),
  map: z.looseObject({ id: z.string(), rows: z.array(z.string()) }),
  ruleset: z.record(z.string(), z.unknown()),
  seed: z.int(),
  players: z.object({ red: LogPlayer, blue: LogPlayer }),
  options: z.record(z.string(), z.unknown()).optional(),
});

// Structural check only; the engine validates the map (R-MAP-*) and every step on replay.
const LogCommon = {
  format: z.literal(LOG_FORMAT),
  rulesVersion: z.string(),
  setup: LogSetup,
  /** Optional in files from before nations existed. */
  nations: Nations.optional(),
  /** Optional in files from before player colours existed. */
  colours: Colours.optional(),
};

/**
 * The exported file: everything needed to replay the game (R-RNG-02, R-RNG-03). Version 1
 * (a browser-only game) has bare actions; version 2 (a server game) has steps.
 */
export const GameLog = z.discriminatedUnion('version', [
  z.object({ ...LogCommon, version: z.literal(1), actions: z.array(GameAction) }),
  z.object({ ...LogCommon, version: z.literal(2), steps: z.array(GameStep) }),
]);
export type GameLog = z.infer<typeof GameLog>;
